import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../api';
import styles from './HomePage.module.css';
import { Helmet } from 'react-helmet';

const RegisterPage: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const navigateToSection = (sectionId: string): void => {
        navigate(`/#${sectionId}`);
    };

    const startNewChat = (): void => {
        navigate('/chat');
    };

    const handleRegister = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }
        try {
            const response = await registerUser(email, password, '');
            if (response.success) {
                navigate('/confirm-code', { state: { email } });
            } else {
                setError(response.message || 'Ошибка регистрации');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Ошибка регистрации. Возможно, email уже занят.');
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className={styles.homeContainer}>
            <Helmet>
                <meta charSet="UTF-8" />
                <title>Регистрация в GPT4Chats - Создайте аккаунт для ИИ-помощника</title>
                <meta
                    name="description"
                    content="Зарегистрируйтесь в GPT4Chats, чтобы получить доступ к ИИ-помощнику на базе GPT-4. Генерация текстов, анализ документов и многое другое."
                />
                <meta
                    name="keywords"
                    content="регистрация, GPT4Chats, ИИ-помощник, GPT-4, создать аккаунт, искусственный интеллект"
                />
                <meta name="robots" content="index, follow" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <link rel="canonical" href="https://gpt4chats.ru/register" />
                <meta property="og:title" content="Регистрация в GPT4Chats - Создайте аккаунт для ИИ-помощника" />
                <meta
                    property="og:description"
                    content="Создайте аккаунт в GPT4Chats и начните использовать мощный ИИ на базе GPT-4 для работы и творчества."
                />
                <meta property="og:url" content="https://gpt4chats.ru/register" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="GPT4Chats" />
            </Helmet>
            <header className={styles.homeHeader}>
                <div className={styles.container}>
                    <div className={styles.headerContent}>
                        <div className={styles.homeLogo}>
                            <h1>
                                GPT4Chats<span>.</span>
                            </h1>
                        </div>
                        <nav className={styles.homeNav}>
                            <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                                Главная
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                                Особенности
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                                Вопросы
                            </button>
                            <button className={styles.startChatBtnSmall} onClick={startNewChat}>
                                Начать
                            </button>
                        </nav>
                    </div>
                </div>
            </header>

            <section className={styles.authSection}>
                <div className={styles.authContainer}>
                    <h1>Регистрация</h1>
                    <p className={styles.authIntro}>
                        Создайте аккаунт GPT4Chats, чтобы получить доступ к ИИ-помощнику.
                    </p>
                    <form onSubmit={handleRegister} className={styles.authForm}>
                        <div>
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                required
                                className={styles.authInput}
                                placeholder="Введите ваш email"
                            />
                        </div>
                        <div>
                            <label htmlFor="password">Пароль</label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                                required
                                className={styles.authInput}
                                placeholder="Введите пароль"
                            />
                        </div>
                        <div>
                            <label htmlFor="confirmPassword">Подтвердите пароль</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                                required
                                className={styles.authInput}
                                placeholder="Подтвердите пароль"
                            />
                        </div>
                        {error && <p className={styles.authError}>{error}</p>}
                        <button type="submit" className={styles.authButton}>
                            Зарегистрироваться
                        </button>
                    </form>
                    <div className={styles.policyFooter}>
                        <button className={styles.authLink} onClick={() => navigate('/login')}>
                            Уже есть аккаунт? Войдите
                        </button>
                        <span className={styles.authSep}>•</span>
                        <button className={styles.authLink} onClick={() => navigate('/')}>
                            На главную
                        </button>
                    </div>
                </div>
            </section>

            <footer className={styles.homeFooter}>
                <div className={styles.container}>
                    <div className={styles.footerContent}>
                        <div className={styles.footerSection}>
                            <h4>
                                GPT4Chats<span>.</span>
                            </h4>
                            <p>Ваш ИИ-помощник для работы и творчества.</p>
                            <p>GPT4Chats.ru</p>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Навигация</h4>
                            <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                                Главная
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                                Особенности
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                                Вопросы
                            </button>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Документы</h4>
                            <button className={styles.navLink} onClick={() => navigate('/privacy-policy')}>
                                Политика конфиденциальности
                            </button>
                            <button className={styles.navLink} onClick={() => navigate('/terms-of-use')}>
                                Условия использования
                            </button>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Контакты</h4>
                            <p>Email: support@gpt4chats.ru</p>
                        </div>
                    </div>
                    <div className={styles.footerBottom}>
                        <p>© {new Date().getFullYear()} GPT4Chats. Все права защищены.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default RegisterPage;